import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Stack, Spinner, Play, Pause, X, CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { folderOptions } from './folderTree';
import { dmsApi } from './dmsApi';
import { formatJST } from '@/utils/datetime';

const OPS = [
  { value: 'ocr', label: '書類の文字を読み取る（OCR）', hint: 'PDF・画像の本文を認識し、検索キーワードも同時に作り直します', scope: 'ocr_missing' },
  { value: 'keywords', label: '検索キーワードを作り直す', hint: 'AIを使わず即時。既に読み取った本文から再抽出します', scope: 'keywords_missing' },
  { value: 'ai_tags', label: 'AIタグを付ける', hint: 'AIを使用します（トークン費用が発生します）', scope: 'ai_tags_missing' },
  { value: 'reindex', label: '検索インデックスを作り直す', hint: '検索結果に反映されないときに実行します', scope: null },
];

const STATUS_LABEL = { running: '実行中', paused: '一時停止', cancelled: '中止', done: '完了' };
const STATUS_STYLE = {
  running: 'bg-sky-50 text-sky-700 border-sky-200',
  paused: 'bg-amber-50 text-amber-800 border-amber-200',
  cancelled: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** フォルダー単位の一括メンテナンス（管理者のみ）— 1つの操作を選んで実行する形にする。 */
export default function BulkOcrCard() {
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('all');
  const [includeSub, setIncludeSub] = useState(true);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [op, setOp] = useState('ocr');
  const [scope, setScope] = useState(null);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => { dmsApi.folders().then(setFolders).catch(() => setFolders([])); }, []);
  useEffect(() => { dmsApi.searchIndexStats().then(setStats).catch(() => setStats(null)); }, [jobs]);

  const fid = folderId === 'all' ? '' : folderId;
  const loadScope = useCallback(async () => {
    try { setScope(await dmsApi.bulkScope(fid, includeSub)); } catch { setScope(null); }
  }, [fid, includeSub]);
  const loadJobs = useCallback(async () => {
    try { setJobs(await dmsApi.bulkJobs()); } catch { setJobs([]); }
  }, []);

  useEffect(() => { loadScope(); }, [loadScope]);
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => {
    if (!jobs.some((j) => j.status === 'running')) return undefined;
    timer.current = setTimeout(() => { loadJobs(); loadScope(); }, 2500);
    return () => clearTimeout(timer.current);
  }, [jobs, loadJobs, loadScope]);

  const current = OPS.find((o) => o.value === op);
  const targetCount = !scope ? 0
    : onlyMissing && current.scope ? scope[current.scope] : scope.documents;
  const folderName = fid ? (folders.find((f) => f.id === fid) || {}).name : 'すべてのフォルダー';

  const run = async () => {
    if (op === 'ai_tags'
      && !window.confirm(`AIタグを最大${targetCount}件の書類に生成します。トークン費用が発生します。実行しますか？`)) return;
    setBusy(true);
    try {
      const j = await dmsApi.bulkStart({
        operation: op, folder_id: fid || null,
        include_subfolders: includeSub, only_missing: onlyMissing,
      });
      toast.success(`${j.operation_label} を開始しました（対象 ${j.total}件）`);
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '開始できませんでした');
    } finally { setBusy(false); }
  };

  const control = async (jid, action) => {
    try { await dmsApi.bulkControl(jid, action); loadJobs(); }
    catch (e) { toast.error(e?.response?.data?.detail || '操作に失敗しました'); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white shadow-sm p-4" data-testid="dms-bulk-card">
      <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5 mb-1">
        <Stack size={16} weight="duotone" className="text-teal-700" /> フォルダー一括処理
      </p>
      <p className="text-sm text-zinc-500 mb-3">
        対象と操作を選んで実行します。進行状況は保存されるため一時停止・再開できます。1件の失敗で全体は止まりません。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs font-semibold text-zinc-600 mb-1">対象フォルダー</p>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="h-9 text-sm" data-testid="dms-bulk-folder"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべてのフォルダー</SelectItem>
              {folderOptions(folders).map((o) => (
                <SelectItem key={o.value} value={o.value} title={o.pathLabel}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-600 mb-1">実行する操作</p>
          <Select value={op} onValueChange={setOp}>
            <SelectTrigger className="h-9 text-sm" data-testid="dms-bulk-operation"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap mb-3">
        <label className="flex items-center gap-1.5 text-sm text-zinc-600">
          <input type="checkbox" checked={includeSub} onChange={(e) => setIncludeSub(e.target.checked)}
            className="h-3.5 w-3.5 accent-teal-600" data-testid="dms-bulk-include-sub" />
          サブフォルダーを含む
        </label>
        <label className="flex items-center gap-1.5 text-sm text-zinc-600">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)}
            className="h-3.5 w-3.5 accent-teal-600" data-testid="dms-bulk-only-missing" />
          未処理のみ
        </label>
      </div>

      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 mb-3" data-testid="dms-bulk-scope">
        <p className="text-sm text-zinc-700">
          <span className="font-medium">{folderName}</span> の
          <span className="font-semibold tabular-nums text-zinc-900"> {targetCount}件 </span>
          が対象です（フォルダー内の書類 {scope?.documents ?? 0}件）
        </p>
        <p className="text-xs text-zinc-500 mt-1">{current.hint}</p>
      </div>

      <Button onClick={run} disabled={busy || !targetCount}
        className="h-10 w-full sm:w-auto bg-teal-700 hover:bg-teal-800 text-sm mb-4"
        data-testid={`dms-bulk-run-${op}`}>
        {busy && <Spinner size={14} className="animate-spin mr-1.5" />}
        {targetCount ? `${targetCount}件に実行する` : '対象がありません'}
      </Button>

      {stats && (
        <div className="rounded-md border border-zinc-200 p-3 mb-4" data-testid="dms-index-stats">
          <p className="text-sm font-semibold text-zinc-700 flex items-center gap-1.5 mb-1">
            <MagnifyingGlass size={14} className="text-zinc-500" /> 検索インデックスの状態
          </p>
          <p className="text-sm text-zinc-600">
            書類 {stats.documents_total}件のうち、本文を読み取り済み {stats.documents_with_text}件 /
            検索に登録済み {stats.documents_indexed}件（意味検索用ベクトル {stats.documents_embedded}件）
          </p>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="space-y-2" data-testid="dms-bulk-jobs">
          <p className="text-sm font-semibold text-zinc-700">実行履歴</p>
          {jobs.slice(0, 6).map((j) => {
            const pct = j.total ? Math.round((j.processed / j.total) * 100) : 0;
            return (
              <div key={j.id} className="rounded-md border border-zinc-200 p-2.5" data-testid={`dms-bulk-job-${j.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`rounded-md border px-1.5 py-0.5 text-xs font-medium ${STATUS_STYLE[j.status] || ''}`}>
                    {STATUS_LABEL[j.status] || j.status}
                  </span>
                  <span className="text-sm font-medium text-zinc-800">{j.operation_label}</span>
                  <span className="text-sm text-zinc-400">{j.folder_name}</span>
                  <span className="ml-auto text-sm tabular-nums text-zinc-500">
                    {j.processed}/{j.total}（成功 {j.ok} · 失敗 {j.failed} · 対象外（処理不要） {j.skipped}）
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden mt-2">
                  <div className={`h-full transition-[width] duration-500 ease-out ${j.status === 'running' ? 'bg-sky-600' : 'bg-teal-700'}`}
                    style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-xs text-zinc-400">{formatJST(j.created_at)}</span>
                  {j.status === 'running' && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-600"
                        onClick={() => control(j.id, 'pause')} data-testid={`dms-bulk-pause-${j.id}`}>
                        <Pause size={12} className="mr-1" />一時停止
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600"
                        onClick={() => control(j.id, 'cancel')} data-testid={`dms-bulk-cancel-${j.id}`}>
                        <X size={12} className="mr-1" />中止
                      </Button>
                      <Spinner size={12} className="animate-spin text-sky-600" />
                    </>
                  )}
                  {['paused', 'cancelled'].includes(j.status) && j.processed < j.total && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-teal-700"
                      onClick={() => control(j.id, 'resume')} data-testid={`dms-bulk-resume-${j.id}`}>
                      <Play size={12} className="mr-1" />再開
                    </Button>
                  )}
                  {j.status === 'done' && <CheckCircle size={12} weight="fill" className="text-emerald-600" />}
                  {j.last_error && <span className="text-xs text-rose-600 truncate">{j.last_error}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
